// Shared pieces of the books screens: money as Riverstone shows it (dollars large, cents small),
// a loader that re-reads after each change, the toast, and the plain "it did not work" message.

import { Children, useCallback, useEffect, useState, type ReactNode } from 'react';
import { RpcError } from '@flowology/client';
import { client } from '../api.ts';
import { useDesign } from '../blueprint/design.ts';
import { closedMonthHint, moneyParts } from './model.ts';
import type { Period } from './types.ts';

export function Money({ cents, signed, className }: { cents: number; signed?: boolean; className?: string }) {
  const p = moneyParts(cents);
  const sign = p.sign || (signed && cents > 0 ? '+' : '');
  return (
    <span className={`money${cents < 0 ? ' neg' : ''}${className ? ` ${className}` : ''}`}>
      {sign}
      {p.whole}
      <small>{p.cents}</small>
    </span>
  );
}

export const errorText = (e: unknown, fallback = 'That did not work. Try again.') =>
  e instanceof RpcError ? e.message : fallback;

/**
 * Calls a query and keeps its answer; `reload` after a change. A null input waits. The last answer
 * stays while a new input loads (`current` says whether it is this input's), but never after a refusal:
 * what the person may no longer see goes from the page (QA-682).
 */
export function useQuery<T>(action: string, input: unknown, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [dataKey, setDataKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const key = JSON.stringify(input);
  const reload = useCallback(async () => {
    if (input === null) return; // not yet: what it needs is still loading
    try {
      setData(await client.call<T>(action, input));
      setDataKey(key);
      setError(null);
    } catch (e) {
      setError(errorText(e, 'Could not load this.'));
      if (e instanceof RpcError && e.code === 'forbidden') {
        setData(null);
        setDataKey(null);
      }
    }
    // The input is compared by value (key), so a new object with the same fields does not reload.
  }, [action, key, ...deps]);
  useEffect(() => {
    void reload();
  }, [reload]);
  return { data, error, reload, current: data !== null && dataKey === key };
}

export interface Act {
  busy: boolean;
  run: <T = unknown>(action: string, input: unknown, done?: string, opts?: ActOpts) => Promise<T | null>;
}
/** `refused` also hears a refusal (after it is said), for a caller that acts on its detail. */
type ActOpts = { idempotencyKey?: string; refused?: (e: unknown) => void };

/** Runs a command, shows what happened, and returns the result (or null when it was refused). */
export function useAct(toast: (m: string) => void, after: () => Promise<void> | void): Act {
  const [busy, setBusy] = useState(false);
  const run = async <T,>(
    action: string,
    input: unknown,
    done?: string,
    opts?: ActOpts,
  ): Promise<T | null> => {
    setBusy(true);
    try {
      const r = await client.call<T>(
        action,
        input,
        opts?.idempotencyKey ? { idempotencyKey: opts.idempotencyKey } : undefined,
      );
      if (done) toast(done);
      await after();
      return r;
    } catch (e) {
      if (e instanceof RpcError && e.code === 'pending_approval')
        toast('Sent for approval: a person decides it.');
      else toast(errorText(e));
      opts?.refused?.(e);
      return null;
    } finally {
      setBusy(false);
    }
  };
  return { busy, run };
}

export function useToast(): [string | null, (m: string) => void] {
  const [toast, setToast] = useState<string | null>(null);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);
  return [toast, setToast];
}

export function Panel({
  title,
  aside,
  children,
  className,
}: {
  title: string;
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`sheet panel${className ? ` ${className}` : ''}`} aria-label={title}>
      <div className="panel-head">
        <h2>{title}</h2>
        {aside}
      </div>
      {children}
    </section>
  );
}

/**
 * A row's actions. In Blueprint a row carries one action (DESIGN.md §3a): the first one shows, the rest
 * open under More (drawn only once opened); Riverstone draws them all, as it always has.
 */
export function RowActions({ children, className }: { children: ReactNode; className?: string }) {
  const blueprint = useDesign() === 'blueprint';
  const [more, setMore] = useState(false);
  const cls = className ? `row-actions ${className}` : 'row-actions';
  if (!blueprint) return <div className={cls}>{children}</div>;
  const [first, ...rest] = Children.toArray(children);
  return (
    <div className={cls}>
      {first}
      {rest.length > 0 && (
        <button type="button" className="bp-more" aria-expanded={more} onClick={() => setMore((m) => !m)}>
          {more ? 'Less' : 'More'}
        </button>
      )}
      {more && rest}
    </div>
  );
}

/** A row's main button: filled in Riverstone; in Blueprint the zone's one primary is its own, so a row's is a word. */
export const useRowPrimary = (): string => (useDesign() === 'blueprint' ? 'btn' : 'btn primary');

// The loading ladder lives in shared/ (Settings, Company and Agents use it too); re-exported for books.
export { Loading } from '../shared/Loading.tsx';

export function Empty({ children }: { children: ReactNode }) {
  return <p className="muted empty">{children}</p>;
}

/** Under a date field: where an entry dated in a closed month posts (Q48, F-37), or nothing. */
export function ClosedMonthHint({ date }: { date: string }) {
  const q = useQuery<{ periods: Period[] }>('periods.list', {});
  const hint = q.data ? closedMonthHint(date, q.data.periods) : null;
  return hint ? (
    <span className="muted small hint block" role="note">
      {hint}.
    </span>
  ) : null;
}
